'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

interface LikelihoodExplainerProps {
  likelihood?: string;
  abstractionLevel: string;
}

interface LikelihoodInfo {
  level: string;
  description: string;
  action: string;
  color: string;
}

const likelihoodData: Record<string, LikelihoodInfo> = {
  High: {
    level: 'High',
    description: 'This weakness is commonly encountered in real-world attacks and is relatively easy to exploit. Attackers actively target systems with this vulnerability.',
    action: 'Prioritize immediate remediation. Implement defensive measures and monitor for exploit attempts.',
    color: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  },
  Medium: {
    level: 'Medium',
    description: 'This weakness can be exploited under certain conditions or requires some skill. It represents a moderate risk that should be addressed.',
    action: 'Plan remediation within your regular development cycle. Implement mitigation controls where possible.',
    color: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
  },
  Low: {
    level: 'Low',
    description: 'This weakness is difficult to exploit or requires specific conditions to be present. The risk is limited but should not be ignored.',
    action: 'Address during routine security reviews. Document the weakness and monitor for changes in threat landscape.',
    color: 'border-green-500 bg-green-50 dark:bg-green-900/20',
  },
};

const abstractionInfo: Record<string, { title: string; description: string }> = {
  Class: {
    title: 'Weakness Class',
    description: 'This is a high-level, abstract weakness type that describes a general category of security issues. Multiple more specific weakness variants may fall under this class.',
  },
  Base: {
    title: 'Base Weakness',
    description: 'This is a more specific weakness that still describes a general concept. It is more detailed than a Class but still abstract enough to cover multiple implementation patterns.',
  },
  Variant: {
    title: 'Weakness Variant',
    description: 'This is a specific, detailed weakness tied to particular technologies, languages, or contexts. It represents a concrete vulnerability pattern.',
  },
  Compound: {
    title: 'Compound Weakness',
    description: 'This weakness is a combination of multiple weaknesses that together create a security vulnerability. Each component weakness contributes to the overall risk.',
  },
};

export default function LikelihoodExplainer({ likelihood, abstractionLevel }: LikelihoodExplainerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const likelihoodInfo = likelihood ? likelihoodData[likelihood] : null;
  const abstraction = abstractionInfo[abstractionLevel] || abstractionInfo.Base;

  const borderColor = likelihoodInfo?.color || 'border-gray-400 bg-gray-50 dark:bg-gray-800/50';

  return (
    <div className={`border-l-4 ${borderColor} rounded-r-lg overflow-hidden`}>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/50 dark:hover:bg-gray-800/30 transition-colors"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-gray-600 dark:text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            What does this weakness mean?
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Abstraction Level Explanation */}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {abstraction.title}
            </h4>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {abstraction.description}
            </p>
          </div>

          {/* Likelihood Explanation */}
          {likelihoodInfo && (
            <div>
              <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {likelihoodInfo.level} Likelihood of Exploit
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {likelihoodInfo.description}
              </p>

              <div className="flex items-start gap-2 p-3 mt-2 bg-white/60 dark:bg-gray-800/40 rounded-lg">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                  Recommended Action:
                </span>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {likelihoodInfo.action}
                </p>
              </div>
            </div>
          )}

          {!likelihoodInfo && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              No specific exploit likelihood data is available for this weakness.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
